import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Field } from '../components/Field';
import { ViewHeader } from '../components/ViewHeader';
import { EntityRow } from '../components/EntityRow';
import { EmptyState } from '../components/EmptyState';
import { useWorkspace } from '../hooks/useWorkspace';
import { useLang } from '../hooks/useLang';
import { useEntityList } from '../hooks/useEntityList';
import { useToast } from '../components/Toast';
import { LIMITS, NATIONS } from '../../domain/constants';
import { newClub } from '../../domain/workspace';
import { t } from '../../i18n';
import type { WorkspaceStore } from '../../domain/store';
import type { Club } from '../../types';

export function ClubsView({ store, hidden }: { store: WorkspaceStore; hidden: boolean }) {
  const workspace = useWorkspace(store);
  useLang();
  const toast = useToast();
  const { editing, startEditing, stopEditing, stopEditingIf } = useEntityList();
  const { clubs } = workspace;

  function handleAdd(): void {
    const c = newClub();
    store.update((p) => p.clubs.push(c));
    startEditing(c.id);
  }

  function handleDelete(id: string): void {
    const used = store.workspace.athletes.filter((a) => a.clubId === id).length;
    if (used > 0 && !confirm(t('clubs.deleteConfirm', { used }))) return;
    store.update((p) => {
      p.clubs = p.clubs.filter((c) => c.id !== id);
      for (const a of p.athletes) if (a.clubId === id) a.clubId = '';
    });
    stopEditingIf(id);
    toast(t('clubs.deleted'));
  }

  return (
    <section className="mx-auto flex max-w-[1180px] flex-col gap-3 p-4" hidden={hidden}>
      <ViewHeader title={t('clubs.title')} lede={t('clubs.lede')} />

      {clubs.length === 0 ? (
        <EmptyState title={t('clubs.empty.title')} text={t('clubs.empty.text')} />
      ) : (
        clubs.map((c) =>
          c.id === editing ? (
            <ClubForm key={c.id} c={c} store={store} onDone={stopEditing} onDelete={() => handleDelete(c.id)} />
          ) : (
            <ClubRow key={c.id} c={c} onEdit={() => startEditing(c.id)} onDelete={() => handleDelete(c.id)} />
          ),
        )
      )}
      <Button className="w-full" onClick={handleAdd}>
        {t('clubs.add')}
      </Button>
    </section>
  );
}

function ClubRow({ c, onEdit, onDelete }: { c: Club; onEdit: () => void; onDelete: () => void }) {
  const contactName = `${c.contact.firstName} ${c.contact.lastName}`.trim();
  return (
    <EntityRow
      title={c.name || t('clubs.unnamed')}
      sub={`${c.nation}${c.federation ? ` · ${c.federation}` : ''}${c.number ? ` · ${t('clubs.row.number', { number: c.number })}` : ''}${contactName ? ` · ${t('clubs.row.contact', { name: contactName })}` : ''}`}
      actions={
        <>
          <Button variant="outline" size="sm" onClick={onEdit}>
            {t('common.edit')}
          </Button>
          <Button variant="destructive" size="sm" onClick={onDelete}>
            {t('common.delete')}
          </Button>
        </>
      }
    />
  );
}

function ClubForm({ c, store, onDone, onDelete }: { c: Club; store: WorkspaceStore; onDone: () => void; onDelete: () => void }) {
  return (
    <Card className="ring-2 ring-ring/40">
      <CardHeader>
        <CardTitle>{t('clubs.form.title')}</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field scope="club" store={store} target={c} spec={{ label: t('clubs.field.name'), key: 'name', maxLength: LIMITS.club, required: true, wide: true }} />
        <Field scope="club" store={store} target={c} commit={false} spec={{ label: t('clubs.field.nation'), key: 'nation', required: true, options: NATIONS.map((n) => [n, n] as const) }} />
        <Field scope="club" store={store} target={c} spec={{ label: t('clubs.field.federation'), key: 'federation', maxLength: LIMITS.federation, placeholder: 'HRV' }} />
        <Field scope="club" store={store} target={c} spec={{ label: t('clubs.field.number'), key: 'number', mono: true, hint: t('common.optional') }} />
      </CardContent>
      <Separator />
      <CardContent className="flex flex-col gap-4">
        <p className="flex items-center gap-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          {t('clubs.contact.title')} <Badge>{t('common.optional')}</Badge>
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field scope="clubContact" store={store} target={c.contact} spec={{ label: t('field.firstName'), key: 'firstName', maxLength: LIMITS.name }} />
          <Field scope="clubContact" store={store} target={c.contact} spec={{ label: t('field.lastName'), key: 'lastName', maxLength: LIMITS.name }} />
          <Field scope="clubContact" store={store} target={c.contact} spec={{ label: t('field.email'), key: 'email', maxLength: LIMITS.email, type: 'email' }} />
          <Field scope="clubContact" store={store} target={c.contact} spec={{ label: t('field.phone'), key: 'phone', maxLength: LIMITS.phone, type: 'tel' }} />
          <Field scope="clubContact" store={store} target={c.contact} spec={{ label: t('field.street'), key: 'street', maxLength: LIMITS.street, wide: true }} />
          <Field scope="clubContact" store={store} target={c.contact} spec={{ label: t('field.postalCode'), key: 'postalCode', maxLength: LIMITS.postalCode, mono: true }} />
          <Field scope="clubContact" store={store} target={c.contact} spec={{ label: t('field.city'), key: 'city', maxLength: LIMITS.city }} />
          <Field scope="clubContact" store={store} target={c.contact} spec={{ label: t('field.fax'), key: 'fax', maxLength: LIMITS.fax }} />
        </div>
      </CardContent>
      <CardFooter>
        <Button onClick={onDone}>{t('common.done')}</Button>
        <Button variant="destructive" onClick={onDelete}>
          {t('common.delete')}
        </Button>
      </CardFooter>
    </Card>
  );
}
